import React, { FC } from 'react'
import Link from 'next/link'
import { Menu } from '@services/api/menus'
import Image from 'next/image'

import { RelationshipCenter } from './components/RelationshipCenter'
import { Socials } from './components/Socials'
import { Certifications } from './components/Certifications'
import { Newsletter } from '@components/shared/newsletter'
import cl from 'classnames'

interface Props {
  menuFooter1: Menu[]
  menuFooter2: Menu[]
  hasSnap?: boolean
}

export const Footer: FC<Props> = ({
  menuFooter1,
  menuFooter2,
  hasSnap = false
}: Props) => (
  <footer
    className={cl(
      'w-full flex flex-col items-center bg-[#F9F9F9] font-myriad antialiased px-4 pb-[45px] md:px-8 md:pb-[26px] lg:px-8 lg:pb-[26px]',
      hasSnap ? 'snap-always snap-start pt-20' : 'pt-8 md:pt-6 lg:pt-[86px]'
    )}
  >
    <div className="w-full max-w-screen-xl flex flex-col border-b border-solid border-v3-gray-100 pb-6 mb-4 md:flex-row md:gap-8 md:pb-8 md:mb-8 lg:gap-[38px]">
      <div className="w-full">
        <Link href="/" aria-label="Ícone Portal Solar">
          <Image
            src="/assets/logo-original.svg"
            alt="Portal Solar - A marca líder em energia solar."
            width={190}
            height={70}
            className="w-[125px] mb-8 md:w-[187px] md:mb-[46px]"
          />
        </Link>

        <p className="text-sm leading-[1.375rem] font-semibold text-v3-gray-400 mb-[27px] md:text-base md:leading-[1.375rem] md:tracking-[-0.2px] md:mb-[25px] lg:max-w-[600px]">
          Somos a marca líder em energia solar do Brasil, uma empresa do grupo
          Solan.
        </p>

        <RelationshipCenter className="hidden md:flex md:flex-col md:gap-4" />
      </div>

      <div className="w-full max-w-[360px] xl:max-w-[420px] md:mt-4">
        <Newsletter className="hidden md:flex md:flex-col md:mb-8" />

        <Socials className="hidden md:flex md:flex-col md:mb-8" />

        <Certifications />
      </div>
    </div>

    <div className="w-full flex flex-col md:hidden">
      <Socials className="border-b border-solid border-v3-gray-100 pb-4 mb-4" />

      <Newsletter className="border-b border-solid border-v3-gray-100 pb-4 mb-4" />

      <RelationshipCenter className="flex flex-col border-b border-solid border-v3-gray-100 gap-4 pb-4 mb-4" />
    </div>

    <div className="w-full max-w-screen-xl flex flex-col border-b border-solid border-v3-gray-100 pb-4 mb-4 md:flex-row md:gap-8 md:pb-8 md:mb-[20px] lg:gap-24 lg:mb-[82px]">
      <div className="w-full md:w-1/2">
        <p className="text-sm font-bold text-v3-gray-400 mb-2 md:text-base md:leading-[1.375rem] md:mb-4">
          Saiba Mais
        </p>

        <div className="grid grid-cols-2 gap-y-2 gap-x-20 items-center md:gap-y-4 md:gap-x-[82px] lg:gap-x-[94px]">
          {menuFooter1?.map(menu =>
            menu.url ? (
              <Link
                key={menu.id}
                href={menu.url}
                target="_blank"
                className="text-sm font-bold leading-[1.375rem] text-v3-gray-300 underline md:text-base md:font-semibold md:leading-[1.375rem]"
              >
                {menu.description}
              </Link>
            ) : (
              <span className="text-sm font-bold leading-[1.375rem] text-v3-gray-300 underline md:text-base md:font-semibold md:leading-[1.375rem]">
                {menu.description}
              </span>
            )
          )}
        </div>
      </div>

      <div className="w-full mt-4 md:w-1/2 md:mt-0">
        <p className="text-sm font-bold text-v3-gray-400 mb-2 md:text-base md:leading-[1.375rem] md:mb-4">
          Links Relacionados
        </p>
        <div className="grid grid-cols-2 gap-y-2 gap-x-20 items-center md:gap-y-4 md:gap-x-[82px] lg:gap-x-[94px]">
          {menuFooter2?.map(menu =>
            menu.url ? (
              <Link
                key={menu.id}
                href={menu.url}
                target="_blank"
                className="text-sm font-bold leading-[1.375rem] text-v3-gray-300 underline md:text-base md:font-semibold md:leading-[1.375rem]"
              >
                {menu.description}
              </Link>
            ) : (
              <span className="text-sm font-bold leading-[1.375rem] text-v3-gray-300 underline md:text-base md:font-semibold md:leading-[1.375rem]">
                {menu.description}
              </span>
            )
          )}
        </div>
      </div>
    </div>

    <p className="w-full max-w-screen-xl text-[0.625rem] leading-[0.875rem] text-v3-gray-300 text-center md:font-semibold md:text-[0.8125rem] md:leading-[1.125rem] md:text-v3-gray-200 md:text-left">
      Portal Solar © 2014-{new Date().getFullYear()}. Todos os direitos
      reservados.
    </p>
  </footer>
)
